
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { 
  OrderStatus, 
  ORDER_STATUS_LABELS, 
  ORDER_STATUS_COLORS, 
  canTransitionTo, 
  updateOrderStatus 
} from "@/utils/orderStatus";
import { CheckCircle, ChevronDown, Clock, Package } from "lucide-react";

interface OrderStatusActionsProps {
  orderId: string;
  status: OrderStatus;
  onStatusChange?: (newStatus: OrderStatus) => void;
  readyAt?: string | null;
  compact?: boolean;
}

export const OrderStatusActions: React.FC<OrderStatusActionsProps> = ({
  orderId,
  status,
  onStatusChange,
  readyAt,
  compact = false
}) => {
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    if (!canTransitionTo(status, newStatus)) return;
    
    setIsUpdating(true);
    try {
      console.log(`Updating order ${orderId} status to ${newStatus}`);
      await updateOrderStatus(orderId, newStatus);
      if (onStatusChange) {
        onStatusChange(newStatus);
      }
    } catch (error) {
      console.error(`Failed to update order ${orderId} status to ${newStatus}:`, error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Status that we can transition to
  const possibleNextStatuses = React.useMemo(() => {
    const statuses: OrderStatus[] = [];
    
    if (canTransitionTo(status, "in_progress")) {
      statuses.push("in_progress");
    }
    
    if (canTransitionTo(status, "ready")) {
      statuses.push("ready");
    }
    
    if (canTransitionTo(status, "completed")) {
      statuses.push("completed");
    }
    
    if (canTransitionTo(status, "cancelled")) {
      statuses.push("cancelled");
    }
    
    return statuses;
  }, [status]);

  const formatReadyTime = (timestamp: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isUpdating || possibleNextStatuses.length === 0}>
            <Button variant="outline" size="sm" className="h-8">
              {isUpdating ? "Updating..." : "Update Status"}
              <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {possibleNextStatuses.map((nextStatus) => (
              <DropdownMenuItem 
                key={nextStatus}
                onClick={() => handleStatusUpdate(nextStatus)}
              >
                {ORDER_STATUS_LABELS[nextStatus]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {readyAt && status === "ready" && (
          <div className="text-xs text-muted-foreground flex items-center">
            <Clock className="mr-1 h-3 w-3" />
            Ready since: {formatReadyTime(readyAt)}
          </div>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        {canTransitionTo(status, "in_progress") && (
          <Button 
            size="sm"
            variant="outline"
            onClick={() => handleStatusUpdate("in_progress")}
            disabled={isUpdating}
            className="flex-1"
          >
            <Clock className="mr-1 h-4 w-4" />
            Start Preparing
          </Button>
        )}
        
        {canTransitionTo(status, "ready") && (
          <Button 
            size="sm"
            variant="outline"
            onClick={() => handleStatusUpdate("ready")}
            disabled={isUpdating}
            className="flex-1 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
          >
            <Package className="mr-1 h-4 w-4" />
            Mark as Ready
          </Button>
        )}
        
        {canTransitionTo(status, "completed") && (
          <Button 
            size="sm"
            variant="outline"
            onClick={() => handleStatusUpdate("completed")}
            disabled={isUpdating}
            className="flex-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          >
            <CheckCircle className="mr-1 h-4 w-4" />
            Complete Order
          </Button>
        )}
      </div>
    </div>
  );
};
